import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useEditorStore, EditorTraining, EditorSequence } from '~/shell/Store';
import { usePreviewTranslationStore } from '~/shell/PreviewTranslationStore';
import { Button } from '~/ui/buttons';
import LightButton from '~/editor/buttons/LightButton';
import { EditorButton } from '~/editor/ui/buttons/EditorButton';
import {
  EditorButtonGroup,
  EditorButtonGroupItem
} from '~/editor/ui/buttons/EditorButtonGroup';
import { editorButtonTokens } from '~/editor/ui/buttons/editorButtonTokens';
import {
  LlamaAi,
  LlamaApps,
  LlamaClose,
  LlamaDesktop,
  LlamaNarration,
  LlamaNarrationApplied,
  LlamaRedo,
  LlamaTranslate,
  LlamaUndo
} from '~/editor/ui/icons';
import NarrationPopup from '~/editor/narration/NarrationPopup';
import { useMessageStore } from '~/editor/store/messageStore';
import MicIcon from '~/assets/editor/mic.svg';
import AIPromptPopup from '~/editor/ai-prompt/AIPromptPopup';
import { usePreview } from '~/editor/hooks/usePreview';
import { usePendingPositionChanges } from '~/editor/hooks/usePendingPositionChanges';
import { Tooltip } from '~/ui/tooltip/Tooltip';
import { Modal } from '~/ui/modal/Modal';
import { Overlay } from '~/ui/overlay/Overlay';
import { x } from '@xstyled/styled-components';
import { getTrainingLocales } from '../translation/getTrainingLocales';
import ModalCloseButton from '~/ui/buttons/ModalCloseButton';
import buildDashboardUrl from '~/lib/url/buildDashboardUrl';
import navigateWithFullPageReload from '~/lib/navigations/navigateWithFullPageReload';
import { useToastStore } from '~/ui/toast/toastStore';
import { ToastSeverity } from '~/ui/toast/types';
import {
  getTrainingSequences,
  hasTrainingTranslationGaps,
  type TranslationTraining
} from './translationGaps';
import { useExitPublishFlow } from './useExitPublishFlow';
import { isTrainingContentInSync } from '../trainingContentTimestamps';
import { DEFAULT_LOCALE } from '~/lib/translations/defaultLocale';
import { savePendingTranslations } from '~/editor/translation/pendingTranslationSave';

const TOOLBAR_ICON_SIZE = 20;
const TOOLBAR_ICON_BUTTON_CENTER = 19;

const PreviewButtonWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const PublishButtonWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;

  /* A disabled button swallows pointer events in some browsers (Chrome), so
     hovering it never reaches the wrapper and the tooltip stays hidden. Let the
     hover pass through to the wrapper — the button is disabled anyway. */
  & button:disabled {
    pointer-events: none;
  }

  &:hover .publishTooltip {
    opacity: 1;
    visibility: visible;
  }
`;

const HINT_INSET_FROM_BUTTON = 8;

const EditorHint = styled.div<{ $startAtButton?: boolean }>`
  position: absolute;
  top: calc(100% + 10px);
  left: ${({ $startAtButton }) =>
    $startAtButton ? `-${HINT_INSET_FROM_BUTTON}px` : '50%'};
  transform: ${({ $startAtButton }) =>
    $startAtButton ? 'none' : 'translateX(-50%)'};
  width: max-content;
  max-width: 260px;
  padding: 4px 12px;
  background-color: #464646;
  color: #fff;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  text-align: center;
  border-radius: 4px;
  z-index: 100;
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease;
  filter: drop-shadow(0 4px 4px rgba(0, 0, 0, 0.16))
    drop-shadow(0 -2px 2px rgba(0, 0, 0, 0.05));

  ::after {
    content: '';
    position: absolute;
    bottom: 100%;
    left: ${({ $startAtButton }) =>
      $startAtButton
        ? `${TOOLBAR_ICON_BUTTON_CENTER + HINT_INSET_FROM_BUTTON}px`
        : '50%'};
    transform: translateX(-50%);
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 6px solid #464646;
  }
`;

const LockedTranslationsTab = styled.div`
  position: relative;
  display: inline-flex;

  & button:disabled {
    pointer-events: none;
  }

  &:hover .lockedTranslationsTooltip {
    opacity: 1;
    visibility: visible;
  }
`;

const Wrapper = styled.div`
  width: 100%;
`;

const FOCUS_RING_ROOM = 4;

const TOOLBAR_SHADOW =
  '0 15px 20px #0000000d, 0 30px 30px #00000008, 0 -2px 4px #0000000d';

const Space = styled.div`
  flex: 1;
  position: fixed;
  margin-top: 10px;
  margin-left: 10px;

  &.leftSpace {
    background-color: #fff;
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px;
    border-radius: 16px;
    box-shadow: ${TOOLBAR_SHADOW};
    z-index: 200;
    box-sizing: border-box;
    height: 58px;

    .undoGroup {
      overflow: hidden;
      box-sizing: content-box;
      width: 75px;
      padding: ${FOCUS_RING_ROOM}px;
      margin: -${FOCUS_RING_ROOM}px;
      opacity: 1;
      transition:
        opacity 0.2s ease,
        width 0.2s ease,
        padding 0.2s ease,
        margin 0.2s ease;
    }

    .undoGroup.hidden {
      width: 0;
      padding: 0;
      margin: 0;
      opacity: 0;
      pointer-events: none;
    }
  }

  &.centerSpace {
    flex: 1;
    left: calc(50% - 180px);
    z-index: 9999;

    .message {
      position: absolute;
      left: 10px;
      padding: 9px;
      z-index: 200;

      border-radius: 16px;
      background: #9738ff;
      width: 370px;
      display: flex;
      align-items: center;
      gap: 10px;

      color: var(--Grey-1, #fff);
      font-family: Raleway;
      font-weight: 700;
      font-size: 16px;
      font-style: normal;
      line-height: 150%;

      text-align: center;

      svg {
        margin-left: 20px;
      }
    }
  }

  &.rightSpace {
    flex: none;
    display: flex;
    align-items: center;
    gap: 8px;
    text-align: right;

    z-index: 200;
    right: 20px;

    background-color: #fff;
    padding: 10px;
    border-radius: 16px;
    box-shadow: ${TOOLBAR_SHADOW};

    .saveAndClose:disabled {
      background-color: ${editorButtonTokens.primary};
      color: ${editorButtonTokens.primaryForeground};
      opacity: 0.75;
      cursor: wait;
    }

    .buttonSlot {
      display: inline-flex;
      align-items: center;
      overflow: hidden;
      flex-shrink: 0;
      box-sizing: content-box;
      padding: ${FOCUS_RING_ROOM}px;
      margin: -${FOCUS_RING_ROOM}px;
      opacity: 1;
      transition:
        opacity 0.2s ease,
        max-width 0.2s ease,
        padding 0.2s ease,
        margin 0.2s ease;
    }

    .buttonSlot.aiSlot {
      max-width: 38px;
    }

    .buttonSlot.speakerSlot {
      max-width: 200px;
    }

    .buttonSlot.hidden {
      max-width: 0;
      padding: 0;
      margin: 0 -8px 0 0;
      opacity: 0;
      pointer-events: none;
    }

    .sequenceIdSection {
      font-size: 12px;
      b {
        font-family: ralewayBold;
      }
    }
  }
`;

interface HeaderProps {
  mode: string;
  setMode: (mode: 'editor' | 'preview' | 'translation') => void;
  exitRedirectUrl: string;
  publishButtonTitle?: string | null;
  publishRedirectUrl?: string | null;
  translationsOnly?: boolean;
  editingMode?: 'private' | 'live' | null;
  translationsLockedUntilPublish?: boolean;
  closeIconOnRight?: boolean;
  showMenu?: boolean;
}

const TranslationAutoUpdateButton = ({
  onClick,
  disabled
}: {
  onClick: () => void;
  disabled?: boolean;
}) => {
  return (
    <Button
      fontFamily="ralewayBold"
      fontSize="14px !important"
      onClick={onClick}
      disabled={disabled}
    >
      Auto-Update
    </Button>
  );
};

const SkipTranslationAutoUpdateButton = ({
  onClick,
  disabled
}: {
  onClick: () => void;
  disabled?: boolean;
}) => {
  return (
    <LightButton
      fontFamily="ralewayBold"
      fontSize="14px !important"
      backgroundColor="transparent"
      onClick={onClick}
      disabled={disabled}
    >
      Update Later
    </LightButton>
  );
};

type TrainingForTranslationModal = {
  status?: 'live' | 'draft' | string;
};

const UpdateTranslationsModal = ({
  open,
  onClose,
  onAutoUpdate,
  onUpdateLater,
  training,
  isLoading,
  leavesEditor
}: {
  open: boolean;
  onClose: () => void;
  onAutoUpdate: () => Promise<void> | void;
  onUpdateLater: () => Promise<void> | void;
  training: TrainingForTranslationModal | null;
  isLoading: boolean;
  leavesEditor: boolean;
}) => {
  if (!open) return null;

  const isAssign = training?.status === 'live';
  const action = leavesEditor
    ? 'leaving'
    : isAssign
      ? 'assigning'
      : 'publishing';
  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  return (
    <Overlay opacity={0.5} open={open} onClick={handleClose}>
      <Modal open borderRadius="21px">
        <x.div maxWidth="600px" paddingTop="1.5rem">
          <ModalCloseButton onClick={handleClose} />

          <x.div padding="20px 60px 30px">
            <x.h2 textAlign="center" paddingBottom="1rem">
              Update translations before {action}?
            </x.h2>

            <x.p textAlign="center" paddingBottom="1rem">
              Some translations may be outdated or missing. To ensure accuracy,
              select how you would like to update them. Any manually edited
              translations will remain unchanged.
            </x.p>

            <x.div display="flex" justifyContent="center" gap="1rem">
              <TranslationAutoUpdateButton
                onClick={() => {
                  void onAutoUpdate();
                }}
                disabled={isLoading}
              />
            </x.div>

            <x.p textAlign="center" paddingTop="1rem">
              <SkipTranslationAutoUpdateButton
                onClick={() => {
                  void onUpdateLater();
                }}
                disabled={isLoading}
              />
            </x.p>
          </x.div>
        </x.div>
      </Modal>
    </Overlay>
  );
};

const PUBLISH_ERROR_MESSAGE =
  'Unable to publish this course. Please try again.';
const TRANSLATION_STATUS_CHECK_ERROR_MESSAGE =
  'Unable to verify translation status. Please try again.';

export const Header = ({
  mode,
  setMode,
  exitRedirectUrl,
  publishButtonTitle,
  publishRedirectUrl,
  translationsOnly = false,
  editingMode = null,
  translationsLockedUntilPublish = false,
  closeIconOnRight = false,
  showMenu = true
}: HeaderProps) => {
  const {
    apiService,
    sequence,
    training,
    undo,
    redo,
    history,
    setSequence,
    setTraining,
    markTrainingPublished,
    forceUpdateSequence,
    setForceUpdateSequence,
    nextSequence,
    prevSequence,
    hasFeatureFlag,
    canUndo: storeCanUndo,
    canRedo: storeCanRedo,
    setUndoRedoState,
    undoAction,
    redoAction,
    shouldDisplayUpdateTranslationsModal,
    setDisplayUpdateTranslationsModal,
    setShouldAutoUpdateTranslations,
    setTranslationScope,
    contentEditable
  } = useEditorStore();
  const { setPreviewTranslationLocale } = usePreviewTranslationStore();
  const [showNarration, setNarration] = useState(false);
  const { showMessage, message } = useMessageStore();
  const [showAIPromptPopup, setAIPromptPopup] = useState(false);
  const { openPreview } = usePreview();
  const pendingPositionChanges = usePendingPositionChanges();
  const [showPreviewTooltip, setShowPreviewTooltip] = useState(false);
  const [publishClicked, setPublishClicked] = useState(false);

  const queryParams = new URLSearchParams(location.search);
  const showPublishButton =
    translationsOnly || queryParams.get('sequence_id') === null;

  const canShowTranslationView = useMemo(() => {
    if (!training) return false;
    const locales = getTrainingLocales(training);
    return locales.some(localeOption => localeOption.locale !== 'en');
  }, [training]);

  const hasTranslationGaps = useMemo(() => {
    return hasTrainingTranslationGaps(
      training as unknown as TranslationTraining
    );
  }, [training]);

  const shouldCheckTranslationsBeforeExit = useMemo(() => {
    if (!training) return false;

    return (
      !translationsOnly &&
      training.status === 'live' &&
      training.shouldDisplayTranslateButton &&
      canShowTranslationView
    );
  }, [training, canShowTranslationView, translationsOnly]);

  useEffect(() => {
    if (
      training &&
      mode === 'translation' &&
      !canShowTranslationView &&
      !translationsOnly
    ) {
      setMode('editor');
    }
  }, [mode, canShowTranslationView, setMode, training, translationsOnly]);

  const shouldAllowAIButton = useMemo(() => {
    return (
      contentEditable &&
      sequence &&
      [
        'Multimedia4',
        'Quiz2',
        'SocialMedia1',
        'Statement2',
        'Statement5'
      ].includes(sequence.templateName as string)
    );
  }, [sequence?.templateName, contentEditable]);

  const [isUndoDisabled, setIsUndoDisabled] = useState(false);
  const [isRedoDisabled, setIsRedoDisabled] = useState(false);

  const showPublishError = () => {
    useToastStore.getState().add({
      severity: ToastSeverity.Error,
      message: PUBLISH_ERROR_MESSAGE
    });
  };

  const showTranslationStatusCheckError = () => {
    useToastStore.getState().add({
      severity: ToastSeverity.Error,
      message: TRANSLATION_STATUS_CHECK_ERROR_MESSAGE
    });
  };

  const handleUndo = async () => {
    if (isUndoDisabled) return;

    setIsRedoDisabled(true);
    setIsUndoDisabled(true);
    setForceUpdateSequence(false);

    const useBackendUndoRedo = hasFeatureFlag(
      'course_editor_backend_undo_redo'
    );

    if (useBackendUndoRedo && sequence) {
      try {
        await undoAction(
          sequence.id,
          getSequenceInfo as (
            sequenceId: number,
            training: EditorTraining
          ) => void
        );
      } catch (error) {
        console.error('Undo failed:', error);
        setIsUndoDisabled(false);
        setIsRedoDisabled(false);
      }
    } else {
      undo(
        getSequenceInfo as (
          sequenceId: number,
          training: EditorTraining
        ) => void
      );
    }
  };

  const handleRedo = async () => {
    if (isRedoDisabled) return;

    setIsRedoDisabled(true);
    setIsUndoDisabled(true);
    setForceUpdateSequence(false);

    const useBackendUndoRedo = hasFeatureFlag(
      'course_editor_backend_undo_redo'
    );

    if (useBackendUndoRedo && sequence) {
      try {
        await redoAction(
          sequence.id,
          getSequenceInfo as (
            sequenceId: number,
            training: EditorTraining
          ) => void
        );
      } catch (error) {
        console.error('Redo failed:', error);
        setIsUndoDisabled(false);
        setIsRedoDisabled(false);
      }
    } else {
      redo(
        getSequenceInfo as (
          sequenceId: number,
          training: EditorTraining
        ) => void
      );
    }
  };

  const getSequenceInfo = (
    sequenceId: number,
    trainingList: TranslationTraining
  ) => {
    if (apiService) {
      apiService
        .getSequenceInfo(sequenceId)
        .then(
          (sequenceResponse: {
            sequence: { id: number };
            sequenceMetadata?: { canUndo: boolean; canRedo: boolean };
          }) => {
            if (!sequenceResponse?.sequence?.id) return;
            setSequence(sequenceResponse.sequence as EditorSequence);

            if (
              sequenceResponse.sequenceMetadata &&
              hasFeatureFlag('course_editor_backend_undo_redo')
            ) {
              setUndoRedoState(
                sequenceResponse.sequenceMetadata.canUndo,
                sequenceResponse.sequenceMetadata.canRedo
              );
            }

            const updatedTraining: TranslationTraining = {
              ...trainingList,
              chapters: (trainingList.chapters || []).map(chapter => ({
                ...chapter,
                chapterSequences: chapter?.chapterSequences?.map(
                  chapterSequence => {
                    return chapterSequence.sequence?.id === sequenceId
                      ? {
                          ...chapterSequence,
                          sequence: {
                            ...chapterSequence.sequence,
                            ...sequenceResponse.sequence
                          }
                        }
                      : chapterSequence;
                  }
                )
              }))
            };
            setTraining(updatedTraining as EditorTraining);
            setForceUpdateSequence(true);

            setIsUndoDisabled(false);
            setIsRedoDisabled(false);
          }
        );
    }
  };

  const { isExitInProgress, publish, onExit } = useExitPublishFlow({
    apiService,
    training: training as unknown as TranslationTraining | null,
    publishRedirectUrl,
    exitRedirectUrl,
    shouldCheckTranslationsBeforeExit:
      shouldCheckTranslationsBeforeExit as boolean,
    hasTranslationGaps,
    setTraining: setTraining as unknown as (
      training: TranslationTraining
    ) => void,
    markTrainingPublished,
    setDisplayUpdateTranslationsModal,
    setShouldAutoUpdateTranslations,
    onError: showTranslationStatusCheckError,
    onPublishError: showPublishError
  });

  const onPublishClick = async () => {
    if (!training) return;

    // Only a first publish needs the right to assign; a republish only updates what learners already have.
    const isRepublishOfDraft =
      training.status === 'draft' && training.isRepublish;
    if (training.canAssignLearners || isRepublishOfDraft) {
      return publish(false, { notifyOnFailure: false, showBusyState: false });
    }

    const redirectUrl = buildDashboardUrl(
      '/companies/:companyId/payment_methods',
      {
        pathParams: {
          companyId: training.companyId,
          trainingId: training.id
        },
        queryParams: {
          mode: 'upgrade_lms',
          plan_package: 'lms_package',
          training_id: training.id,
          upgrade: true
        }
      }
    );

    return navigateWithFullPageReload(redirectUrl);
  };

  const canWeDoRedo = () => {
    const useBackendUndoRedo = hasFeatureFlag(
      'course_editor_backend_undo_redo'
    );

    if (useBackendUndoRedo) {
      return storeCanRedo;
    }

    if (forceUpdateSequence) {
      return false;
    }

    const currentHistory = history.filter(
      item => item.sequenceId == sequence!.id
    );

    if (currentHistory.length == 0) {
      return false;
    }

    const activeIndex = currentHistory.findIndex(item => item.active === true);
    return activeIndex >= 0 && activeIndex < currentHistory.length - 1;
  };

  const canWeDoUndo = () => {
    const useBackendUndoRedo = hasFeatureFlag(
      'course_editor_backend_undo_redo'
    );

    if (useBackendUndoRedo) {
      return storeCanUndo;
    }

    if (forceUpdateSequence) {
      return false;
    }

    const currentHistory = history.filter(
      item => item.sequenceId == sequence!.id
    );

    if (currentHistory.length == 0) {
      return false;
    }

    const activeIndex = currentHistory.findIndex(item => item.active === true);
    return activeIndex > 0;
  };

  useEffect(() => {
    if (translationsOnly) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const activeElement = document.activeElement;
      const isInputElement =
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        (activeElement as HTMLElement)?.contentEditable === 'true' ||
        activeElement?.getAttribute('contenteditable') === 'true';

      if (isInputElement) {
        return;
      }

      if (!training || !sequence) return;

      const allSequences = getTrainingSequences(
        training as unknown as TranslationTraining
      );

      if (allSequences.length === 0) return;

      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          if (sequence.id === allSequences[allSequences.length - 1].id) {
            return;
          }
          event.preventDefault();
          nextSequence();
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          if (sequence.id === allSequences[0].id) {
            return;
          }
          event.preventDefault();
          prevSequence();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [training, sequence, translationsOnly]);

  const isPublishAction = mode !== 'translation';
  const isPublishBlocked = Boolean(
    isPublishAction &&
    training &&
    training.status === 'draft' &&
    training.isRepublish &&
    isTrainingContentInSync(training)
  );

  const getPublishButtonTitle = () => {
    if (publishButtonTitle) return publishButtonTitle;
    if (training!.status !== 'live') return 'Publish';
    if (training!.canAssignLearners) return 'Assign';

    return 'Upgrade to Assign';
  };

  const getPrimaryButtonLabel = () => {
    // "Saving..." only while an actual save/publish is running — not when
    // publish is merely blocked because the custom draft has no edits (CAT-677).
    if (isExitInProgress) return 'Saving...';
    if (mode === 'translation')
      return translationsOnly ? 'Close' : 'Save and Close';
    return getPublishButtonTitle();
  };

  const isLiveTraining = training?.status === 'live';
  const primaryButtonLeavesLiveEditor =
    editingMode === 'live' && Boolean(publishButtonTitle);
  const showCloseIconOnLeft = !(closeIconOnRight && showPublishButton);
  const showCloseIconOnRight =
    closeIconOnRight &&
    showPublishButton &&
    !primaryButtonLeavesLiveEditor &&
    !translationsOnly;
  const showLeftToolbar = !translationsOnly || showCloseIconOnLeft;
  const leaveIconLabel =
    editingMode === 'private' ? 'Leave without publishing' : 'Close editor';
  const isEditorMode = mode === 'editor';
  const actionButtonClassName =
    mode === 'translation' ? 'saveAndClose' : undefined;

  const closeTranslationsOnlySession = async () => {
    if (await savePendingTranslations()) {
      await onExit({ skipPublish: true });
    }
  };

  const leaveEditor = () =>
    translationsOnly ? closeTranslationsOnlySession() : onExit();

  const onPrimaryButtonClick = () => {
    if (mode === 'translation') {
      if (translationsOnly) {
        void closeTranslationsOnlySession();
        return;
      }
      setMode('editor');
      return;
    }

    if (primaryButtonLeavesLiveEditor) {
      onExit();
      return;
    }

    if (isLiveTraining && training?.canAssignLearners) {
      onExit({ requirePublish: true });
      return;
    }

    onPublishClick();
  };

  const onEditorModeSwitchClick = () => {
    setPreviewTranslationLocale(DEFAULT_LOCALE);
    setMode('editor');
  };

  return (
    <Wrapper data-translations-only={translationsOnly}>
      {showLeftToolbar && (
        <Space className="leftSpace">
          {showCloseIconOnLeft && (
            <EditorButton
              iconOnly
              className="exitBtn"
              aria-label={leaveIconLabel}
              onClick={() =>
                translationsOnly ? closeTranslationsOnlySession() : onExit()
              }
            >
              <LlamaClose size={TOOLBAR_ICON_SIZE} />
            </EditorButton>
          )}

          {!translationsOnly && (
            <EditorButtonGroup className="modeGroup" aria-label="Editor mode">
              <EditorButtonGroupItem
                aria-label="Edit mode"
                active={mode === 'editor'}
                onClick={onEditorModeSwitchClick}
              >
                <LlamaDesktop size={TOOLBAR_ICON_SIZE} />
              </EditorButtonGroupItem>
              <EditorButtonGroupItem
                aria-label="Preview mode"
                active={mode === 'preview'}
                onClick={() => setMode('preview')}
              >
                <LlamaApps size={TOOLBAR_ICON_SIZE} />
              </EditorButtonGroupItem>
              {translationsLockedUntilPublish ? (
                <LockedTranslationsTab>
                  <EditorButtonGroupItem aria-label="Translations" disabled>
                    <LlamaTranslate size={TOOLBAR_ICON_SIZE} />
                  </EditorButtonGroupItem>
                  <EditorHint
                    className="lockedTranslationsTooltip"
                    $startAtButton={!showMenu}
                  >
                    Translations will run automatically once course is published
                  </EditorHint>
                </LockedTranslationsTab>
              ) : (
                canShowTranslationView && (
                  <EditorButtonGroupItem
                    aria-label="Translations"
                    active={mode === 'translation'}
                    onClick={() => {
                      if (sequence?.id) {
                        setTranslationScope({
                          kind: 'sequence',
                          sequenceId: sequence.id
                        });
                      }
                      setMode('translation');
                    }}
                  >
                    <LlamaTranslate size={TOOLBAR_ICON_SIZE} />
                  </EditorButtonGroupItem>
                )
              )}
            </EditorButtonGroup>
          )}
          {!translationsOnly && (
            <EditorButtonGroup
              className={isEditorMode ? 'undoGroup' : 'undoGroup hidden'}
              aria-label="History"
            >
              <EditorButtonGroupItem
                className="arrow arrow-left"
                aria-label="Undo"
                onClick={handleUndo}
                disabled={
                  !isEditorMode ||
                  !contentEditable ||
                  !canWeDoUndo() ||
                  isUndoDisabled
                }
              >
                <LlamaUndo size={TOOLBAR_ICON_SIZE} />
              </EditorButtonGroupItem>
              <EditorButtonGroupItem
                className="arrow arrow-right"
                aria-label="Redo"
                onClick={handleRedo}
                disabled={
                  !isEditorMode ||
                  !contentEditable ||
                  !canWeDoRedo() ||
                  isRedoDisabled
                }
              >
                <LlamaRedo size={TOOLBAR_ICON_SIZE} />
              </EditorButtonGroupItem>
            </EditorButtonGroup>
          )}
        </Space>
      )}
      {!translationsOnly && (
        <Space className="centerSpace">
          {showMessage && (
            <div className="message">
              <MicIcon />
              <p>{message}</p>
            </div>
          )}
        </Space>
      )}
      <Space className="rightSpace">
        {!translationsOnly && shouldAllowAIButton && (
          <div
            className={
              isEditorMode ? 'buttonSlot aiSlot' : 'buttonSlot aiSlot hidden'
            }
          >
            <EditorButton
              iconOnly
              aria-label="AI"
              onClick={() => setAIPromptPopup(true)}
              className="ai"
              disabled={!isEditorMode}
            >
              <LlamaAi size={TOOLBAR_ICON_SIZE} />
            </EditorButton>
          </div>
        )}
        {!translationsOnly && (
          <UpdateTranslationsModal
            open={shouldDisplayUpdateTranslationsModal}
            onClose={() => setDisplayUpdateTranslationsModal(false)}
            onAutoUpdate={() => publish(true)}
            onUpdateLater={() => publish(false)}
            training={training}
            isLoading={isExitInProgress}
            leavesEditor={Boolean(isLiveTraining && publishButtonTitle)}
          />
        )}
        {!translationsOnly &&
          contentEditable &&
          sequence &&
          sequence.templateName != 'Multimedia2' && (
            <div
              className={
                isEditorMode
                  ? 'buttonSlot speakerSlot'
                  : 'buttonSlot speakerSlot hidden'
              }
            >
              <EditorButton
                data-tour="slide-narration"
                onClick={() => setNarration(!showNarration)}
                disabled={!isEditorMode}
              >
                {sequence.narration ? (
                  <LlamaNarrationApplied size={TOOLBAR_ICON_SIZE} />
                ) : (
                  <LlamaNarration size={TOOLBAR_ICON_SIZE} />
                )}
                Slide Narration
              </EditorButton>
            </div>
          )}
        {!translationsOnly && (
          <PreviewButtonWrapper
            onMouseEnter={() =>
              pendingPositionChanges > 0 && setShowPreviewTooltip(true)
            }
            onMouseLeave={() => setShowPreviewTooltip(false)}
          >
            <EditorButton
              onClick={() => openPreview()}
              disabled={pendingPositionChanges > 0}
            >
              Preview
            </EditorButton>
            {showPreviewTooltip && pendingPositionChanges > 0 && (
              <Tooltip placement="bottom-center-up" top="50px" right="0">
                Saving changes... Please wait.
              </Tooltip>
            )}
          </PreviewButtonWrapper>
        )}
        {showPublishButton ? (
          <>
            <PublishButtonWrapper>
              <EditorButton
                data-tour="primary-action"
                variant="primary"
                className={actionButtonClassName}
                onClick={() => {
                  setPublishClicked(true);
                  onPrimaryButtonClick();
                }}
                disabled={isExitInProgress || isPublishBlocked}
              >
                {getPrimaryButtonLabel()}
              </EditorButton>
              {isPublishBlocked && !isExitInProgress && !publishClicked && (
                <EditorHint className="publishTooltip">
                  Edit course to enable publishing
                </EditorHint>
              )}
            </PublishButtonWrapper>
            {showCloseIconOnRight && (
              <EditorButton
                iconOnly
                aria-label={leaveIconLabel}
                onClick={leaveEditor}
              >
                <LlamaClose size={TOOLBAR_ICON_SIZE} />
              </EditorButton>
            )}
          </>
        ) : (
          <span className="sequenceIdSection">
            {' '}
            Sequence ID: <b>{sequence?.id}</b>
          </span>
        )}
        {showNarration &&
          contentEditable &&
          createPortal(
            <NarrationPopup onClose={() => setNarration(false)} />,
            document.body
          )}
        {showAIPromptPopup &&
          contentEditable &&
          createPortal(
            <AIPromptPopup onClose={() => setAIPromptPopup(false)} />,
            document.body
          )}
      </Space>
    </Wrapper>
  );
};
